/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import type { Molecule } from './Molecule';

// TIP3P water (Jorgensen et al., J. Chem. Phys. 79, 926, 1983) for the Molecular
// Modeling engine. The general-purpose parameters every other molecule gets give
// water Gasteiger charges (O −0.41, H +0.21) and UFF oxygen (ε 0.06 kcal/mol): a
// hydrogen bond about a quarter of the real one, so the lab's water was a gas at
// room temperature and its liquid only held together near 100 K. With TIP3P's
// charges and its single oxygen–oxygen Lennard-Jones site the same engine keeps a
// liquid at 300 K that evaporates as it heats (no vapor at 300 K, a few molecules
// at 350 K, more at 400 K in a 48-molecule pool). Bonded terms (flexible O–H
// stretch and H–O–H bend) stay the MMFF-lite ones every molecule gets.
export const TIP3P = {
  chargeO: -0.834,
  chargeH: 0.417,
  sigmaO: 3.15061, // Å
  epsilonO: 0.1521 * 0.0433641, // kcal/mol → eV
} as const;

const isElement = (symbol: string | undefined, e: string) => (symbol ?? '').toUpperCase() === e;

// A molecule that is exactly one water: three atoms, one oxygen and two
// hydrogens, each hydrogen bonded to the oxygen. Radial bonds decide when the
// molecule has them; without bonds (a raw coordinate set) the O–H distances do.
export const isWaterMolecule = (m: Molecule): boolean => {
  if (m.atoms.length !== 3) return false;
  const o = m.atoms.find((a) => isElement(a.elementSymbol, 'O'));
  const hs = m.atoms.filter((a) => isElement(a.elementSymbol, 'H'));
  if (!o || hs.length !== 2) return false;
  if (m.radialBonds.length > 0) {
    return hs.every((h) =>
      m.radialBonds.some((b) => (b.atom1 === o && b.atom2 === h) || (b.atom1 === h && b.atom2 === o)),
    );
  }
  return hs.every((h) => h.position.distanceTo(o.position) < 1.3);
};

// Give every water molecule the TIP3P charges and Lennard-Jones parameters
// (hydrogens carry charge only). Idempotent; returns how many molecules it set.
// The MolecularDynamics constructor applies the same entry through the species
// table (speciesForceField.ts, which imports TIP3P from here), so every engine
// build — the lab's mirror, the worker's copy, headless tools and seeders — gets
// the same water. A charge the user set by hand (Atom.chargeSet) is kept.
export const applyWaterModel = (molecules: Molecule[]): number => {
  let n = 0;
  for (const m of molecules) {
    if (!isWaterMolecule(m)) continue;
    for (const a of m.atoms) {
      if (isElement(a.elementSymbol, 'O')) {
        if (!a.chargeSet) a.charge = TIP3P.chargeO;
        a.sigma = TIP3P.sigmaO;
        a.epsilon = TIP3P.epsilonO;
      } else {
        if (!a.chargeSet) a.charge = TIP3P.chargeH;
        a.epsilon = 0;
      }
    }
    n++;
  }
  return n;
};
